/**
 * The pacing governor's types. Mirrors /v1/ops/quota (migration 030).
 *
 * The one thing to hold on to when reading these: the weekly window DOES NOT
 * ROLL OVER. That is why there is a `floor` as well as a `ceiling`, and why the
 * panel treats "behind" as a real problem rather than as thrift.
 */

export type QuotaStatus =
  | "calibrating"
  | "on_pace"
  | "behind"
  | "ahead"
  | "front_loaded"
  | "session_hold"
  | "reserve"
  | "exhausted";

export interface QuotaState {
  now: string;
  timezone: string;
  plan: string;
  calibrating: boolean;

  weekStart: string;
  weekEnd: string;
  deadline: string;
  secsToDeadline: number;
  secsToReset: number;
  t: number;

  weeklyBudget: number;
  target: number;
  spent: number;
  spentPct: number | null;

  ideal: number;
  floor: number;
  ceiling: number;
  hardCeiling: number;
  pace: number | null;
  projectedEnd: number | null;

  daySpent: number;
  dayCap: number;
  sessionSpent: number;
  sessionCap: number;
  sessionResetsAt: string | null;

  inFlight: number;
  status: QuotaStatus;
  headroom: number;
}

export interface QuotaConfig {
  plan: string;
  timezone: string;
  resetDow: number;
  resetHour: number;
  resetMinute: number;
  weeklyBudget: number;
  calibrating: boolean;
  targetPct: number;
  reserveHours: number;
  corridorEarly: number;
  corridorLate: number;
  maxDayPct: number;
  sessionPct: number;
  sessionMins: number;
  catchupMaxWorkers: number;
  autoAnchor: boolean;
  anchorObservedAt: string | null;
  lastRateLimitAt: string | null;
  rateLimitStreak: number;
  updatedAt: string;
  updatedBy: string | null;
}

export interface QuotaDecision {
  workers: number;
  delta: number;
  drawSurplus: boolean;
  hold: boolean;
  holdUntil: string | null;
  reason: string;
  sprintRefused: boolean;
}

export interface QuotaEvent {
  id: number;
  at: string;
  kind: string;
  workers: number | null;
  pace: number | null;
  spent: number | null;
  note: string;
  detail: unknown;
}

export interface SurplusItem {
  id: string;
  title: string;
  detail: string;
  streamId: string | null;
  agentId: string | null;
  position: number;
  enabled: boolean;
  lastUsedAt: string | null;
  timesUsed: number;
}

export interface BurndownPoint {
  at: string;
  spent: number;
  ideal: number;
  floor: number;
  ceiling: number;
}

export interface QuotaSnapshot {
  state: QuotaState;
  config: QuotaConfig;
  decision: QuotaDecision;
  readyCards: number;
}

/** How each status reads on the panel, and how alarmed to be about it. */
export const QUOTA_STATUS: Record<QuotaStatus, { label: string; tone: "good" | "warn" | "bad" | "" }> = {
  calibrating:  { label: "CALIBRATING",  tone: "" },
  on_pace:      { label: "ON PACE",      tone: "good" },
  behind:       { label: "UNDER-USING",  tone: "warn" },
  ahead:        { label: "RUNNING HOT",  tone: "warn" },
  front_loaded: { label: "FRONT-LOADED", tone: "bad" },
  session_hold: { label: "5H WINDOW HELD", tone: "warn" },
  reserve:      { label: "IN RESERVE",   tone: "" },
  exhausted:    { label: "WEEK SPENT",   tone: "bad" },
};
